import React from 'react';
import { 
  ExternalLink, 
  Globe, 
  ShieldCheck, 
  Trophy, 
  CheckCircle2, 
  Users, 
  GraduationCap, 
  Award, 
  Sparkles,
  Briefcase
} from 'lucide-react';
import { SUMMIT_CONFIG } from '../config/summitConfig';

interface RegistrationSectionProps {
  reducedMotion?: boolean;
  preselectedCommittee?: string;
}

export const RegistrationSection: React.FC<RegistrationSectionProps> = ({
  preselectedCommittee,
}) => {
  const isPreselectedAuction = preselectedCommittee === 'IPL' || preselectedCommittee === 'FIFA' || preselectedCommittee?.includes('AUCTION');
  const isPreselectedConventional = Boolean(
    preselectedCommittee && !isPreselectedAuction
  );

  const conventionalCommittees = [
    { code: 'UNGA', name: 'United Nations General Assembly', num: '01' },
    { code: 'ECOSOC', name: 'UN Economic and Social Council', num: '02' },
    { code: 'LOK SABHA', name: 'House of the People', num: '03' },
    { code: 'AIPPM', name: 'All India Political Parties Meet', num: '04' },
    { code: 'RAJPUTANA YUDDH PARISHAD', name: 'Continuous Crisis Committee', num: '05' },
    { code: 'DEAD POETS SOCIETY', name: 'Continuous Crisis Committee', num: '06' },
    { code: 'IP', name: 'International Press Corps', num: '07' },
  ];

  const auctionCommittees = [
    { code: 'FIFA', name: 'Mega Auction Simulation', num: '08' },
    { code: 'IPL', name: 'Indian Premier League Auction', num: '09' },
  ];

  return (
    <section 
      id="registration" 
      className="relative py-28 sm:py-36 bg-gradient-to-b from-deepblack via-[#0d0d0d] to-deepblack border-t border-white/5 overflow-hidden"
    >
      {/* Background Architectural Watermark */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02] flex items-center justify-center font-serif text-[28vw] font-bold text-beige select-none">
        ENROLL
      </div>

      <div className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32 relative z-10">
        
        {/* Section Pre-title */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-[1px] bg-bronze" />
          <span className="text-xs uppercase tracking-architectural text-bronze font-semibold">
            OFFICIAL DELEGATE REGISTRATION
          </span>
          <div className="w-12 h-[1px] bg-bronze" />
        </div>

        {/* Section Heading */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-14 sm:mb-16 gap-6">
          <div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ivory">
              SELECT YOUR <br />
              <span className="text-beige font-light italic">REGISTRATION TRACK</span>
            </h2>
            <p className="text-ivory/60 text-sm sm:text-base font-light max-w-2xl mt-4 leading-relaxed">
              {SUMMIT_CONFIG.conferenceName} ({SUMMIT_CONFIG.dates}) applications are divided into two specialized tracks. 
              Select your preferred committee group below to complete your official delegation application via Google Forms.
            </p>
          </div>

          <div className="flex items-center gap-3 text-xs tracking-widest uppercase text-beige/80 bg-charcoal-subtle border border-bronze/30 px-5 py-3 rounded-sm self-start lg:self-end">
            <ShieldCheck className="w-4 h-4 text-beige" />
            <span>OFFICIAL GOOGLE FORMS VERIFIED</span>
          </div>
        </div>

        {/* 2-Option Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          
          {/* OPTION 1: CONVENTIONAL COMMITTEES */}
          <div 
            className={`marble-card rounded-sm p-7 sm:p-10 flex flex-col h-full relative transition-all duration-300 border ${
              isPreselectedConventional 
                ? 'border-beige ring-1 ring-beige shadow-glow-bronze' 
                : 'border-bronze/40 hover:border-beige/70'
            }`}
          >
            {/* Greek Key Header Trim */}
            <div className="w-full h-1.5 greek-key-border opacity-50 mb-6" />

            <div className="flex-1 flex flex-col">
              {/* Badge & Track Indicator */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <span className="text-[10px] font-mono font-bold tracking-widest text-bronze uppercase px-3 py-1 bg-white/5 border border-bronze/30 rounded-sm">
                  OPTION 01 • TRACK A
                </span>
                <span className="text-[10px] uppercase tracking-widest text-beige flex items-center gap-1.5 font-semibold">
                  <Globe className="w-3.5 h-3.5 text-bronze" />
                  DIPLOMATIC, CRISIS & PARLIAMENTARY
                </span>
              </div>

              {/* Title */}
              <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-ivory tracking-wide mb-3">
                CONVENTIONAL COMMITTEES
              </h3>

              <p className="text-xs sm:text-sm text-ivory/70 font-light leading-relaxed mb-6">
                Represent sovereign member states, national political leaders, investigative press agencies, or navigate real-time historical and specialized crises.
              </p>

              {/* Committees List */}
              <div className="mb-8">
                <span className="text-[10px] uppercase tracking-architectural text-bronze font-semibold block mb-3">
                  INCLUDED CHAMBERS (07 COMMITTEES)
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {conventionalCommittees.map(item => (
                    <div 
                      key={item.code}
                      className={`p-3 rounded-sm border text-xs flex items-center gap-3 transition-colors ${
                        preselectedCommittee?.includes(item.code) 
                          ? 'border-beige bg-beige/10 text-beige font-semibold' 
                          : 'border-white/5 bg-black/40 text-ivory/80'
                      }`}
                    >
                      <span className="font-mono text-bronze font-bold text-[11px]">{item.num}</span>
                      <div className="overflow-hidden">
                        <span className="font-bold text-ivory block leading-tight">{item.code}</span>
                        <span className="text-[10px] text-ivory/50 truncate block">{item.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Highlights */}
              <div className="space-y-2 mb-8 border-t border-white/5 pt-5 text-xs text-ivory/75 font-light">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-beige shrink-0" />
                  <span>Comprehensive background guides & parliamentary dossiers provided</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-beige shrink-0" />
                  <span>Executive Board mentorship & verified participation credentials</span>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="mt-auto pt-6 border-t border-bronze/30">
              <a
                href={SUMMIT_CONFIG.registrationLinks.conventionalCommittees}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 px-6 bg-gradient-to-r from-beige-300 via-beige to-beige-400 text-deepblack text-xs sm:text-sm uppercase tracking-widest font-bold rounded-sm border border-beige hover:shadow-glow-bronze transition-all flex items-center justify-center gap-2 group shadow-xl"
                data-cursor-interactive
                data-cursor-text="APPLY"
              >
                <span>REGISTER FOR CONVENTIONAL COMMITTEES</span>
                <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <span className="text-[10px] text-ivory/40 text-center block mt-2.5">
                Opens official Google Form for UNGA, ECOSOC, Lok Sabha, AIPPM, Rajputana Yuddh Parishad, Dead Poets Society & IP
              </span>
            </div>
          </div>

          {/* OPTION 2: AUCTION COMMITTEES */}
          <div 
            className={`marble-card rounded-sm p-7 sm:p-10 flex flex-col h-full relative transition-all duration-300 border ${
              isPreselectedAuction 
                ? 'border-beige ring-1 ring-beige shadow-glow-bronze' 
                : 'border-bronze/40 hover:border-beige/70'
            }`}
          >
            {/* Greek Key Header Trim */}
            <div className="w-full h-1.5 greek-key-border opacity-50 mb-6" />

            <div className="flex-1 flex flex-col">
              {/* Badge & Track Indicator */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <span className="text-[10px] font-mono font-bold tracking-widest text-bronze uppercase px-3 py-1 bg-white/5 border border-bronze/30 rounded-sm">
                  OPTION 02 • TRACK B
                </span>
                <span className="text-[10px] uppercase tracking-widest text-beige flex items-center gap-1.5 font-semibold">
                  <Trophy className="w-3.5 h-3.5 text-bronze" />
                  AUCTION & SPORTS GOVERNANCE
                </span>
              </div>

              {/* Title */}
              <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-ivory tracking-wide mb-3">
                AUCTION COMMITTEES
              </h3>

              <p className="text-xs sm:text-sm text-ivory/70 font-light leading-relaxed mb-6">
                Step into the high-stakes world of sports management, live player auction purse bidding, dynamic war-room trading, and tactical squad building.
              </p>

              {/* Committees List */}
              <div className="mb-8">
                <span className="text-[10px] uppercase tracking-architectural text-bronze font-semibold block mb-3">
                  INCLUDED CHAMBERS (02 COMMITTEES)
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {auctionCommittees.map(item => (
                    <div 
                      key={item.code}
                      className={`p-3 rounded-sm border text-xs flex items-center gap-3 transition-colors ${
                        preselectedCommittee?.includes(item.code) 
                          ? 'border-beige bg-beige/10 text-beige font-semibold' 
                          : 'border-white/5 bg-black/40 text-ivory/80'
                      }`}
                    >
                      <span className="font-mono text-bronze font-bold text-[11px]">{item.num}</span>
                      <div className="overflow-hidden">
                        <span className="font-bold text-ivory block leading-tight">{item.code}</span>
                        <span className="text-[10px] text-ivory/50 truncate block">{item.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Highlights */}
              <div className="space-y-2 mb-8 border-t border-white/5 pt-5 text-xs text-ivory/75 font-light">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-beige shrink-0" />
                  <span>Real-time live auction bidding wars, salary purse rules & transfer windows</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-beige shrink-0" />
                  <span>Dynamic valuation systems, tactical scouting dossiers & championship trophies</span>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="mt-auto pt-6 border-t border-bronze/30">
              <a
                href={SUMMIT_CONFIG.registrationLinks.auctionCommittees}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 px-6 bg-gradient-to-r from-beige-300 via-beige to-beige-400 text-deepblack text-xs sm:text-sm uppercase tracking-widest font-bold rounded-sm border border-beige hover:shadow-glow-bronze transition-all flex items-center justify-center gap-2 group shadow-xl"
                data-cursor-interactive
                data-cursor-text="APPLY"
              >
                <span>REGISTER FOR AUCTION COMMITTEES</span>
                <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <span className="text-[10px] text-ivory/40 text-center block mt-2.5">
                Opens official Google Form for FIFA (Mega Auction) & IPL (Auction)
              </span>
            </div>
          </div>

        </div>

        {/* ========================================================
            NEW SECTION: ORGANISING COMMITTEE & CAMPUS AMBASSADOR FORMS
            ======================================================== */}
        <div id="recruitment" className="mt-20 sm:mt-28 scroll-mt-24">
          
          {/* Classical Divider */}
          <div className="mb-14 sm:mb-16 w-full flex items-center justify-center gap-4">
            <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent via-bronze/40 to-transparent" />
            <div className="w-3 h-3 rotate-45 border border-bronze/60 bg-deepblack flex items-center justify-center">
              <div className="w-1 h-1 bg-beige rounded-full" />
            </div>
            <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent via-bronze/40 to-transparent" />
          </div>

          {/* Section Pre-title */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-[1px] bg-bronze" />
            <span className="text-xs uppercase tracking-architectural text-bronze font-semibold">
              LEADERSHIP & OUTREACH • RECRUITMENT CALL
            </span>
            <div className="w-12 h-[1px] bg-bronze" />
          </div>

          {/* Section Heading & Subheading */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-bronze/40 rounded-sm mb-3">
                <Sparkles className="w-3.5 h-3.5 text-beige" />
                <span className="text-[10px] uppercase font-mono tracking-widest text-beige font-bold">
                  APPLICATIONS NOW OPEN
                </span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ivory">
                ORGANISING COMMITTEE & <br />
                <span className="text-beige font-light italic">CAMPUS AMBASSADOR FORMS</span>
              </h2>
            </div>
            <div className="max-w-lg">
              <p className="text-sm text-ivory/70 font-light leading-relaxed">
                Step into the executive machinery of {SUMMIT_CONFIG.conferenceName}. Whether orchestrating summit operations and logistics or leading institutional delegations across campuses, apply to lead.
              </p>
            </div>
          </div>

          {/* 2 Recruitment Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
            
            {/* CARD 1: ORGANISING COMMITTEE */}
            <div className="marble-card rounded-sm p-7 sm:p-10 flex flex-col h-full relative transition-all duration-300 border border-bronze/40 hover:border-beige/70 group shadow-xl">
              {/* Greek Key Header Trim */}
              <div className="w-full h-1.5 greek-key-border opacity-50 mb-6" />

              <div className="flex-1 flex flex-col">
                {/* Badge & Track Indicator */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <span className="text-[10px] font-mono font-bold tracking-widest text-bronze uppercase px-3 py-1 bg-white/5 border border-bronze/30 rounded-sm">
                    CALL 01 • OPERATIONS
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-beige flex items-center gap-1.5 font-semibold">
                    <Users className="w-3.5 h-3.5 text-bronze" />
                    SECRETARIAT & SUMMIT EXECUTIVE CADRE
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-ivory tracking-wide mb-3">
                  ORGANISING COMMITTEE
                </h3>

                <p className="text-xs sm:text-sm text-ivory/70 font-light leading-relaxed mb-6">
                  Join the core executive machinery of {SUMMIT_CONFIG.conferenceName}. Lead conference operations, delegate relations, hospitality, sponsorships, media productions, and chamber crisis management.
                </p>

                {/* Key Roles / Departments */}
                <div className="mb-8">
                  <span className="text-[10px] uppercase tracking-architectural text-bronze font-semibold block mb-3">
                    AVAILABLE EXECUTIVE DEPARTMENTS
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {[
                      { role: 'Secretariat Operations', desc: 'Conference Governance' },
                      { role: 'Delegate Affairs', desc: 'Chamber Coordination' },
                      { role: 'Logistics & Hospitality', desc: 'Venue Management' },
                      { role: 'Public Relations & Media', desc: 'Press & Coverage' },
                    ].map((dept) => (
                      <div 
                        key={dept.role}
                        className="p-3 rounded-sm border border-white/5 bg-black/40 text-ivory/80 text-xs flex items-center gap-3"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-bronze" />
                        <div className="overflow-hidden">
                          <span className="font-bold text-ivory block leading-tight">{dept.role}</span>
                          <span className="text-[10px] text-ivory/50 block truncate">{dept.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Highlights */}
                <div className="space-y-2 mb-8 border-t border-white/5 pt-5 text-xs text-ivory/75 font-light">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-beige shrink-0" />
                    <span>Direct involvement in summit planning, venue management, and committee coordination</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-beige shrink-0" />
                    <span>Executive networking with diplomats, advisors, institutional leaders, and university guests</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-beige shrink-0" />
                    <span>Official Secretariat Certificate of Excellence and executive letters of recommendation</span>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div className="mt-auto pt-6 border-t border-bronze/30">
                <a
                  href={SUMMIT_CONFIG.registrationLinks.organisingCommittee}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-4 px-6 bg-gradient-to-r from-beige-300 via-beige to-beige-400 text-deepblack text-xs sm:text-sm uppercase tracking-widest font-bold rounded-sm border border-beige hover:shadow-glow-bronze transition-all flex items-center justify-center gap-2 group shadow-xl"
                  data-cursor-interactive
                  data-cursor-text="APPLY"
                >
                  <span>APPLY FOR ORGANISING COMMITTEE</span>
                  <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <span className="text-[10px] text-ivory/40 text-center block mt-2.5">
                  Opens official Google Form for Organising Committee Recruitment
                </span>
              </div>
            </div>

            {/* CARD 2: CAMPUS AMBASSADOR */}
            <div className="marble-card rounded-sm p-7 sm:p-10 flex flex-col h-full relative transition-all duration-300 border border-bronze/40 hover:border-beige/70 group shadow-xl">
              {/* Greek Key Header Trim */}
              <div className="w-full h-1.5 greek-key-border opacity-50 mb-6" />

              <div className="flex-1 flex flex-col">
                {/* Badge & Track Indicator */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <span className="text-[10px] font-mono font-bold tracking-widest text-bronze uppercase px-3 py-1 bg-white/5 border border-bronze/30 rounded-sm">
                    CALL 02 • OUTREACH
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-beige flex items-center gap-1.5 font-semibold">
                    <GraduationCap className="w-3.5 h-3.5 text-bronze" />
                    INSTITUTIONAL LIAISON & DELEGATION HEAD
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-ivory tracking-wide mb-3">
                  CAMPUS AMBASSADOR
                </h3>

                <p className="text-xs sm:text-sm text-ivory/70 font-light leading-relaxed mb-6">
                  Represent {SUMMIT_CONFIG.conferenceName} at your school, college, or university campus. Spearhead institutional outreach, build student delegations, and foster peer diplomacy across regional campuses.
                </p>

                {/* Key Benefits / Opportunities */}
                <div className="mb-8">
                  <span className="text-[10px] uppercase tracking-architectural text-bronze font-semibold block mb-3">
                    AMBASSADOR REWARDS & PERKS
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {[
                      { role: 'Delegation Leadership', desc: 'Lead campus student cohorts' },
                      { role: 'Registration Waivers', desc: 'Performance-based incentives' },
                      { role: 'Direct Secretariat Access', desc: 'Priority committee seats' },
                      { role: 'Executive Credentials', desc: 'Verified Letters of Merit' },
                    ].map((perk) => (
                      <div 
                        key={perk.role}
                        className="p-3 rounded-sm border border-white/5 bg-black/40 text-ivory/80 text-xs flex items-center gap-3"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-bronze" />
                        <div className="overflow-hidden">
                          <span className="font-bold text-ivory block leading-tight">{perk.role}</span>
                          <span className="text-[10px] text-ivory/50 block truncate">{perk.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Highlights */}
                <div className="space-y-2 mb-8 border-t border-white/5 pt-5 text-xs text-ivory/75 font-light">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-beige shrink-0" />
                    <span>Official Campus Ambassador Appointment Letter and verified Certificate of Leadership</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-beige shrink-0" />
                    <span>Exclusive delegation perks, fee concessions, and performance-based incentive rewards</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-beige shrink-0" />
                    <span>Direct access to the Secretariat, priority committee allocations, and networking summits</span>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div className="mt-auto pt-6 border-t border-bronze/30">
                <a
                  href={SUMMIT_CONFIG.registrationLinks.campusAmbassador}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-4 px-6 bg-gradient-to-r from-beige-300 via-beige to-beige-400 text-deepblack text-xs sm:text-sm uppercase tracking-widest font-bold rounded-sm border border-beige hover:shadow-glow-bronze transition-all flex items-center justify-center gap-2 group shadow-xl"
                  data-cursor-interactive
                  data-cursor-text="APPLY"
                >
                  <span>APPLY FOR CAMPUS AMBASSADOR</span>
                  <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <span className="text-[10px] text-ivory/40 text-center block mt-2.5">
                  Opens official Google Form for Campus Ambassador Recruitment
                </span>
              </div>
            </div>

          </div>

        </div>

        {/* Bottom Assurance Note */}
        <div className="mt-14 p-6 sm:p-8 bg-charcoal-subtle/60 border border-bronze/20 rounded-sm flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full border border-bronze/40 text-beige shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <span className="text-xs uppercase tracking-widest text-beige font-semibold block">
                DIRECT APPLICATION PORTAL
              </span>
              <p className="text-xs text-ivory/60 font-light mt-0.5">
                Submissions are securely processed by the FLSxMUN 2.0 Secretariat through official Google Forms. Confirmations will be sent via email.
              </p>
            </div>
          </div>
          <a
            href={`mailto:${SUMMIT_CONFIG.contactEmail}`}
            className="text-xs uppercase tracking-widest text-bronze hover:text-beige underline shrink-0"
          >
            Need Assistance? Contact Secretariat →
          </a>
        </div>

      </div>
    </section>
  );
};
